/**
 * useClinicLeads — Thin API data-fetching layer for clinic CRM leads.
 *
 * All domain logic lives in the backend.
 * This hook only: fetches data, exposes API actions, manages loading/error UI state.
 */
import { useState, useCallback } from 'react';
import { clinicLeadAPI } from '../utils/api';

export function useClinicLeads(clinicId) {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── Fetch leads from backend ────────────────────────────────
  const fetchLeads = useCallback(async (params = {}) => {
    if (!clinicId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await clinicLeadAPI.listLeads(clinicId, { limit: 200, ...params });
      setLeads(res.data.leads || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('[useClinicLeads] fetchLeads error:', err);
      setError(err.response?.data?.detail || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [clinicId]);

  // ── Create lead ─────────────────────────────────────────────
  const createLead = useCallback(async (data) => {
    if (!clinicId) throw new Error('No clinicId');
    const res = await clinicLeadAPI.createLead(clinicId, data);
    return res.data;
  }, [clinicId]);

  // ── Status ──────────────────────────────────────────────────
  const updateStatus = useCallback(async (leadId, status, reason) => {
    const res = await clinicLeadAPI.updateStatus(leadId, status, reason);
    return res.data;
  }, []);

  // ── Assignment ──────────────────────────────────────────────
  const assignLead = useCallback(async (leadId, agentId, agentName) => {
    const res = await clinicLeadAPI.assignLead(leadId, agentId, agentName);
    return res.data;
  }, []);

  // ── Notes ───────────────────────────────────────────────────
  const addNote = useCallback(async (leadId, content, noteType) => {
    const res = await clinicLeadAPI.addNote(leadId, content, noteType);
    return res.data;
  }, []);

  const listNotes = useCallback(async (leadId) => {
    const res = await clinicLeadAPI.listNotes(leadId);
    return res.data;
  }, []);

  // ── Messages ────────────────────────────────────────────────
  const addMessage = useCallback(async (leadId, content, direction, messageType) => {
    const res = await clinicLeadAPI.addMessage(leadId, content, direction, messageType);
    return res.data;
  }, []);

  const listMessages = useCallback(async (leadId) => {
    const res = await clinicLeadAPI.listMessages(leadId);
    return res.data;
  }, []);

  // ── Offers ──────────────────────────────────────────────────
  const createOffer = useCallback(async (leadId, data) => {
    const res = await clinicLeadAPI.createOffer(leadId, data);
    return res.data;
  }, []);

  const listOffers = useCallback(async (leadId) => {
    const res = await clinicLeadAPI.listOffers(leadId);
    return res.data;
  }, []);

  const getLatestOffer = useCallback(async (leadId) => {
    const res = await clinicLeadAPI.getLatestOffer(leadId);
    return res.data;
  }, []);

  // ── Invitations ─────────────────────────────────────────────
  const sendInvite = useCallback(async (leadId, customMessage, resend = false) => {
    const res = await clinicLeadAPI.sendInvite(leadId, customMessage, resend);
    return res.data;
  }, []);

  // ── Medical files ───────────────────────────────────────────
  const uploadMedicalFile = useCallback(async (leadId, file, category, notes) => {
    const res = await clinicLeadAPI.uploadMedicalFile(leadId, file, category, notes);
    return res.data;
  }, []);

  const listMedicalFiles = useCallback(async (leadId) => {
    const res = await clinicLeadAPI.listMedicalFiles(leadId);
    return res.data;
  }, []);

  return {
    leads,
    total,
    loading,
    error,
    fetchLeads,
    createLead,
    updateStatus,
    assignLead,
    addNote,
    listNotes,
    addMessage,
    listMessages,
    createOffer,
    listOffers,
    getLatestOffer,
    sendInvite,
    uploadMedicalFile,
    listMedicalFiles,
  };
}
